import { useMemo, useRef } from 'react';
import { useAnnotationDraft } from '@/features/comment-on-file';
import type { AnnotationTool } from '@/features/comment-on-file';
import type { DraftShape } from '@/features/comment-on-file';
import { useShapeDrawing } from '../AssetPreviewOverlay/hooks/useShapeDrawing';
import { usePersistedEntries } from '../AssetPreviewOverlay/hooks/usePersistedEntries';
import { ArrowMarker } from '../AssetPreviewOverlay/shapes/ArrowShape';
import { arrowMarkerId } from '../AssetPreviewOverlay/shapes/ArrowShape.utils';
import { ShapeRenderer } from '../AssetPreviewOverlay/shapes/ShapeRenderer';

const ENABLED_DRAWING_TOOLS = [
  'circle',
  'rectangle',
  'pen',
  'arrow',
] as const satisfies readonly AnnotationTool[];

interface Props {
  page: number;
}

/**
 * Per-PDF-page annotation surface. The SVG fills its parent page wrapper
 * (inset: 0) and uses fractional 0–1 coordinates so shape data is
 * resolution-independent. Each draft shape carries its own page, so this
 * overlay only renders the drafts whose page matches its own.
 */
export function PdfPageOverlay({ page }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { draftShapes, draftsVisible } = useAnnotationDraft();

  const pageMode = useMemo(() => ({ kind: 'pdf' as const, page }), [page]);
  const persisted = usePersistedEntries(pageMode);

  const drawing = useShapeDrawing({
    svgRef,
    enabledTools: ENABLED_DRAWING_TOOLS,
    page,
  });

  // Stabilize identity across renders so the `arrowColors` useMemo dep
  // array doesn't invalidate every render (without this, the conditional
  // produces a fresh `[]` reference each time).
  const renderedDrafts = useMemo(
    () => (!draftsVisible ? [] : draftShapes.filter((d: DraftShape) => d.page === page)),
    [draftsVisible, draftShapes, page],
  );

  const arrowColors = useMemo(() => {
    const set = new Set<string>();
    for (const { shape, color } of persisted) {
      if (shape.type === 'arrow') set.add(color);
    }
    for (const draft of renderedDrafts) {
      if (draft.shape.type === 'arrow') set.add(draft.color);
    }
    return Array.from(set);
  }, [persisted, renderedDrafts]);

  return (
    <svg
      ref={svgRef}
      data-page={page}
      className="absolute inset-0 size-full"
      style={{
        pointerEvents: drawing.isActive ? 'auto' : 'none',
        cursor: drawing.isActive ? 'crosshair' : undefined,
        touchAction: drawing.isActive ? 'none' : undefined,
      }}
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      onPointerDown={drawing.onPointerDown}
      onPointerMove={drawing.onPointerMove}
      onPointerUp={drawing.onPointerUp}
      onPointerCancel={drawing.onPointerCancel}
    >
      {arrowColors.length > 0 && (
        <defs>
          {arrowColors.map((c) => (
            <ArrowMarker key={c} id={arrowMarkerId(c)} color={c} />
          ))}
        </defs>
      )}
      <g data-layer="persisted">
        {persisted.map(({ commentId, shape, color }, i) => (
          <ShapeRenderer key={`${commentId}-${i}`} shape={shape} color={color} />
        ))}
      </g>
      {renderedDrafts.length > 0 && (
        <g data-layer="draft" style={{ pointerEvents: 'none' }}>
          {renderedDrafts.map((draft, i) => (
            <ShapeRenderer key={i} shape={draft.shape} color={draft.color} />
          ))}
        </g>
      )}
    </svg>
  );
}
